import React, { useCallback, useEffect, useState, useRef } from "react";
import type { ParsedDisclaimerType } from "../../../../hooks/shared/useDisclaimerContent";
import { BnbLabel } from "../../../../utils/labels";
import * as DisclaimerStyles from "../../../../../../../common-deprecated/styles/v2/toyota/components/Disclaimer";
import Icon from "../../../../../../../common-deprecated/styles/v2/toyota/globals/Icon";
import { ToggleIconWrapper } from "../../../../../../../common-deprecated/styles/v2/toyota/globals/IconWrapper";
import { getNumberFromPx, useTheme } from "../../../../../../../common-deprecated/themes/common";
import { useWindowDimensions } from "../../../../../../../common-deprecated/hooks";

type DisclaimerType = {
    disclaimer: ParsedDisclaimerType;
    hasDarkBackground?: boolean;
};

const Disclaimer = (props: DisclaimerType): JSX.Element | null => {
    const { collapsible, collapseMobileDisclaimers, id, name, index, disclaimer, from, until } = props.disclaimer;

    // Two pass render, only if there is a possibility that the disclaimer could be collapsed based on collapseMobileDisclaimers which is based on window width.
    const [renderComponent, setRenderComponent] = useState<boolean>(collapsible || !collapseMobileDisclaimers);
    const [collapsed, setCollapsed] = useState<boolean>(true);
    const [shouldCollapse, setShouldCollapse] = useState<boolean>(collapsible || false);
    const { breakpoints } = useTheme();
    const [height, setHeight] = useState(0);
    const contentRef = useRef<HTMLDivElement>(null);

    const windowWidth = useWindowDimensions().width;
    useEffect(() => {
        if (contentRef.current) {
            const elementHeight = contentRef.current.getBoundingClientRect().height;
            setHeight(elementHeight);
        }

        if (!renderComponent) setRenderComponent(true);
        // If collapseMobileDisclaimers is true and the device is mobile, set shouldCollapse true.
        if (!collapsible && windowWidth <= getNumberFromPx(breakpoints.lg) && collapseMobileDisclaimers && height > 70)
            setShouldCollapse(true);
    }, [windowWidth, renderComponent, collapsible, collapseMobileDisclaimers]);

    const toggleCollapsed = useCallback(() => setCollapsed((state) => !state), []);
    if (!renderComponent || !disclaimer) return null;

    let dateLabel;
    if (from && until) dateLabel = <BnbLabel label="promoFromUntilDate" params={{ from, until }} />;
    else if (from && !until) dateLabel = <BnbLabel label="promoFromDate" params={{ from }} />;
    else if (!from && until) dateLabel = <BnbLabel label="promoUntilDate" params={{ until }} />;

    // IMPORTANT: Disclaimers can contain all sorts of weird text content (p tags, div tags,...) so to avoid HTML structure errors make sure that they are injected in divs.
    const disclaimerOutput = (
        <DisclaimerStyles.Text ref={contentRef} data-cpdqm="ignore">
            {dateLabel && <p>{dateLabel}</p>}
            {/* DO NOT CHANGE THE DIV WITH DANGEROUS CONTENT TO OTHER TYPES AS THIS TRIGGERS HYDRATION ISSUES */}
            <div dangerouslySetInnerHTML={{ __html: disclaimer }} />
        </DisclaimerStyles.Text>
    );

    return (
        <DisclaimerStyles.Wrapper
            id={id} // Used for scrolling down to disclaimer
            genericDisclaimer={id === "genericDisclaimer"}
            shouldCollapse={shouldCollapse}
            collapsed={collapsed}
            fullHeight={height}
            hasDarkBackground={props.hasDarkBackground}
            role="listitem"
        >
            {name && (
                <DisclaimerStyles.Title>
                    {index && <DisclaimerStyles.Badge>{index}</DisclaimerStyles.Badge>}
                    <span>{name}</span>
                </DisclaimerStyles.Title>
            )}
            {disclaimerOutput}

            {shouldCollapse && (
                <DisclaimerStyles.Button type="button" onClick={shouldCollapse ? toggleCollapsed : undefined}>
                    {collapsed ? (
                        <BnbLabel label="T1.readMore" fallbackLabel="readMore" />
                    ) : (
                        <BnbLabel label="T1.readLess" fallbackLabel="readLess" />
                    )}
                    <ToggleIconWrapper isDown={!collapsed}>
                        <Icon variant="chevronDown" />
                    </ToggleIconWrapper>
                </DisclaimerStyles.Button>
            )}
        </DisclaimerStyles.Wrapper>
    );
};

export default Disclaimer;
